/**
 * protocol.js — the wire format, shared verbatim by the client and the relay.
 *
 * ONE FILE, BOTH ENDS. A protocol written twice is a protocol that will disagree, and the
 * failure mode is silent: a field added on one side reads as garbage on the other rather than
 * as an error. Plain ESM JavaScript so the Node relay can import it with no build step, with
 * a hand-written .d.ts so the TypeScript client still gets types.
 *
 * TWO CHANNELS, CHOSEN BY FRAME TYPE
 *   text frames    JSON. Rare, structured, and shape-changing: hello, welcome, join, leave.
 *   binary frames  Fixed-layout poses. Frequent, uniform, and hot.
 *
 * That split is the whole design. Control messages are a handful per session and readability
 * is worth more than bytes; poses are twelve a second per shark forever and the reverse is
 * true. Using the frame type to tell them apart means no tag byte and no ambiguity.
 *
 * WHAT IS NOT SENT
 * No geometry, ever. A shark is a species index and a seed — the mesh is deterministic from
 * those, so every client builds every other client's animal locally. Nor is the world sent:
 * the coast is analytic and the reef streams from (worldSeed, cell), so one seed in the
 * welcome packet puts everybody in the identical ocean.
 *
 * No bank, no tail phase, no fin pose. All of it is derivable from position and heading over
 * time, and deriving it locally at full frame rate looks better than receiving it at twelve.
 */

export const PROTOCOL_VERSION = 5;

/** how often each client publishes its pose, and the relay broadcasts, in Hz */
export const POSE_HZ = 12;

/* ---- the feeder --------------------------------------------------------------------------
   The one contested thing in the game, and therefore the only thing the relay arbitrates.

   It is deliberately the SMALLEST possible authority: the server owns a counter and the answer
   to "who claimed it first", and nothing else. It does not simulate the fish, does not check
   whether a claimer was really in reach, and does not care — there is no advantage worth
   cheating for in being followed by a decorative fish, and building an anti-cheat for it would
   cost more than the feature.

   The POSITION is derived from the world seed and the feeder's id on both ends rather than
   sent, so a client that misses the announcement and catches the next one is still looking at
   the same fish in the same place. */

/** seconds between feeders; the relay rolls a number in this range */
export const FEEDER_MIN_S = 40;
export const FEEDER_MAX_S = 75;

/**
 * Clean a player-supplied shark name.
 *
 * Names are the only thing one player can put in front of another's eyes, so this is a trust
 * boundary and not a formatting nicety. Control characters and the bidi overrides go, because
 * they let a string reorder or hide text around it; length is capped so a name cannot be used
 * as a billboard; and the result is trimmed so whitespace cannot fake an empty one. Rendering
 * is via textContent everywhere, so markup is inert regardless — this is about display, not
 * injection.
 */
export const NAME_MAX = 18;
export function cleanName(raw) {
  if (typeof raw !== 'string') return '';
  let out = '';
  for (const ch of raw.normalize('NFC')) {
    const c = ch.codePointAt(0);
    if (c < 0x20 || c === 0x7f) continue;                       // control
    if (c >= 0x200b && c <= 0x200f) continue;                   // zero width + bidi marks
    if (c >= 0x202a && c <= 0x202e) continue;                   // bidi overrides
    if (c >= 0x2066 && c <= 0x2069) continue;                   // isolates
    out += ch;
    if (out.length >= NAME_MAX) break;
  }
  return out.trim();
}

/* ---- bays --------------------------------------------------------------------------------
   A bay is a room, and its CODE IS ITS WHOLE IDENTITY. The world seed is derived from the
   code rather than assigned by the relay, which is worth more than it looks: a link carries
   everything needed to be in the same ocean as the person who sent it, the relay stores no
   per-room state and so has nothing to lose on a restart, and two people who type the same
   code are in the same place whether or not a server was ever involved.

   The alphabet has no O/0, I/1/L, S/5 or U/V. A code that has to survive being read down a
   phone or typed off a screenshot cannot afford the pairs people confuse. */
export const BAY_ALPHABET = '23456789ABCDEFGHJKMNPQRTWXYZ';
export const BAY_LEN = 6;

/** Canonical form, or '' if it is not a bay code. Forgiving about case, spaces and dashes. */
export function normaliseBay(raw) {
  if (typeof raw !== 'string') return '';
  const up = raw.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (up.length !== BAY_LEN) return '';
  for (const ch of up) if (!BAY_ALPHABET.includes(ch)) return '';
  return up;
}

/** How a code is shown to a human: one dash, because six unbroken characters read as noise. */
export function formatBay(code) {
  return code.length === BAY_LEN ? `${code.slice(0, 3)}-${code.slice(3)}` : code;
}

/**
 * The reef a bay code means. FNV-1a: tiny, well-distributed, and identical in every
 * implementation — which matters here more than quality, because the client and the relay
 * must agree on the answer bit for bit or the two of them build different oceans.
 */
export function bayWorldSeed(code) {
  let h = 0x811c9dc5;
  for (let i = 0; i < code.length; i++) {
    h ^= code.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  // 0 is the "no seed" sentinel everywhere else in this codebase; never hand it back.
  return h === 0 ? 1 : h;
}

/** bytes per shark in a snapshot: id + x,y,z + heading + speed */
export const POSE_BYTES = 2 + 4 * 5;

/**
 * Encode this client's own pose. No id: the relay knows which socket it came from, and a
 * client that could name itself could name someone else.
 */
export function encodePose(out, p) {
  const view = new DataView(out);
  view.setFloat32(0, p.x, true);
  view.setFloat32(4, p.y, true);
  view.setFloat32(8, p.z, true);
  view.setFloat32(12, p.heading, true);
  view.setFloat32(16, p.speed, true);
  return out;
}

export function decodePose(buf) {
  const view = new DataView(buf);
  return {
    x: view.getFloat32(0, true),
    y: view.getFloat32(4, true),
    z: view.getFloat32(8, true),
    heading: view.getFloat32(12, true),
    speed: view.getFloat32(16, true),
  };
}

/** one client's own pose, without the id */
export const OWN_POSE_BYTES = 4 * 5;

/** bytes before the first pose: when it was sent, then how many follow */
export const SNAPSHOT_HEADER = 6;

/**
 * Pack every peer into one frame.
 *
 * One packet per client per tick rather than one per peer per tick. With N clients the naive
 * relay sends N^2 frames a tick; this sends N. At twelve ticks a second that is the
 * difference between a hobby box holding fifty sharks and falling over at twelve.
 *
 * THE TIMESTAMP IS THE IMPORTANT FIELD, and it was missing for a version. Without it a client
 * can only date a snapshot by when it ARRIVED, which bakes network jitter straight into the
 * playback timeline: packets sent 83 ms apart that arrive 60 and 110 ms apart describe a peer
 * covering the same ground in different amounts of time, so it visibly speeds up and slows
 * down. Four bytes of sender clock make the axis uniform and the motion smooth, and they also
 * make packet LOSS distinguishable from lateness, which arrival times cannot.
 *
 * Milliseconds since the relay started, uint32: wraps after 49 days of uptime, and the client
 * treats a large backwards jump as a restart and resynchronises.
 */
export function encodeSnapshot(entries, sentAt) {
  const buf = new ArrayBuffer(SNAPSHOT_HEADER + entries.length * POSE_BYTES);
  const view = new DataView(buf);
  view.setUint32(0, sentAt >>> 0, true);
  view.setUint16(4, entries.length, true);
  let o = SNAPSHOT_HEADER;
  for (const e of entries) {
    view.setUint16(o, e.id, true);
    view.setFloat32(o + 2, e.x, true);
    view.setFloat32(o + 6, e.y, true);
    view.setFloat32(o + 10, e.z, true);
    view.setFloat32(o + 14, e.heading, true);
    view.setFloat32(o + 18, e.speed, true);
    o += POSE_BYTES;
  }
  return buf;
}

/** @returns {{sentAt:number, poses:Array}} sentAt is the relay's clock, in milliseconds */
export function decodeSnapshot(buf) {
  const view = new DataView(buf);
  const sentAt = view.getUint32(0, true);
  const n = view.getUint16(4, true);
  const out = [];
  let o = SNAPSHOT_HEADER;
  for (let i = 0; i < n; i++) {
    out.push({
      id: view.getUint16(o, true),
      x: view.getFloat32(o + 2, true),
      y: view.getFloat32(o + 6, true),
      z: view.getFloat32(o + 10, true),
      heading: view.getFloat32(o + 14, true),
      speed: view.getFloat32(o + 18, true),
    });
    o += POSE_BYTES;
  }
  return { sentAt, poses: out };
}
